import React, { createContext, useCallback, useContext, useState } from 'react';
import { usePracticeLead } from '../hooks/usePracticeLead';

export interface TysonModeState {
	isActive: boolean;
	sessionPracticedCompanyIds: Set<string>;
}

export interface TysonModeActions {
	toggleTysonMode: () => void;
	enterTysonMode: () => void;
	exitTysonMode: () => void;
	addPracticedCompanyId: (companyId: string) => void;
	isPracticedCompany: (companyId: string) => boolean;
	getNextPracticeLead: (ruleId?: string) => Promise<any>;
	isLoadingPracticeLead: boolean;
}

export interface TysonModeContextValue extends TysonModeState, TysonModeActions {}

const TysonModeContext = createContext<TysonModeContextValue | null>(null);

export const TysonModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [isActive, setIsActive] = useState(false);
	const [sessionPracticedCompanyIds, setSessionPracticedCompanyIds] = useState<Set<string>>(new Set());

	// Only call the hook when we actually need it (when Tyson mode is active)
	const practiceLeadMutation = usePracticeLead();

	const addPracticedCompanyId = useCallback((companyId: string) => {
		setSessionPracticedCompanyIds(prev => new Set(prev).add(companyId));
	}, []);

	const toggleTysonMode = useCallback(() => {
		setIsActive(prev => {
			if (prev) {
				setSessionPracticedCompanyIds(new Set());
			}
			return !prev;
		});
	}, []);

	const enterTysonMode = useCallback(() => {
		setIsActive(true);
		// Tyson mode is now activated - the Queue container will handle loading practice leads
	}, []);

	const exitTysonMode = useCallback(() => {
		setIsActive(false);
		setSessionPracticedCompanyIds(new Set());
	}, []);

	const isPracticedCompany = useCallback(
		(companyId: string) => sessionPracticedCompanyIds.has(companyId),
		[sessionPracticedCompanyIds]
	);

	const getNextPracticeLead = useCallback(
		async (ruleId?: string) => {
			if (!isActive) {
				throw new Error('Tyson mode is not active');
			}

			const previousCompanyIds = Array.from(sessionPracticedCompanyIds);

			const result = await practiceLeadMutation.mutateAsync({
				previousCompanyIds,
				ruleId,
			});

			if (result?.company?.id) {
				addPracticedCompanyId(result.company.id);
			}

			return result;
		},
		[isActive, sessionPracticedCompanyIds, practiceLeadMutation, addPracticedCompanyId]
	);

	const value: TysonModeContextValue = {
		isActive,
		sessionPracticedCompanyIds,
		toggleTysonMode,
		enterTysonMode,
		exitTysonMode,
		addPracticedCompanyId,
		isPracticedCompany,
		getNextPracticeLead,
		isLoadingPracticeLead: practiceLeadMutation.isPending,
	};

	return <TysonModeContext.Provider value={value}>{children}</TysonModeContext.Provider>;
};

export const useTysonMode = (): TysonModeContextValue => {
	const context = useContext(TysonModeContext);
	if (!context) {
		throw new Error('useTysonMode must be used within a TysonModeProvider');
	}
	return context;
};
